import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Alert,
  CardContent,
  Stack,
  Skeleton,
  Button,
  Divider,
  Chip,
} from '@mui/material';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
// import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   ResponsiveContainer,
// } from 'recharts';
// import InstitutionPageHeader from '../../components/InstitutionPageHeader';
import DashboardMetricCard from '../../components/Cards/DashboardMetricCard';
import GlassCard from '../../components/Cards/GlassCard';
import { ctGetBatchInsights } from '../../services/api';
import useAuthStore from '../../store/authStore';
import { formatAcademicDashboardChipLabel } from '../../utils/academicProfileFromApi';
import { getBatches } from '../../services/batchService';
// import {  PRIMARY,   dashboardTooltipSurfaceSx } from '../../utils/colors';

const NEVER_SUBMITTED_DAYS = 999999;

const compareIsoWeekKeys = (a, b) => {
  const [ya, wa] = a.split('-W').map(Number);
  const [yb, wb] = b.split('-W').map(Number);
  if (ya !== yb) return ya - yb;
  return wa - wb;
};

function mergeWeeklyProgress(insightsPayloads) {
  const map = new Map();
  for (const data of insightsPayloads) {
    for (const row of data?.weeklyProgress ?? []) {
      const w = row.week;
      if (!w) continue;
      map.set(w, (map.get(w) || 0) + (Number(row.solved) || 0));
    }
  }
  return [...map.entries()]
    .map(([week, solved]) => ({ week, label: week.replace(/^\d{4}-W/, 'W'), solved }))
    .sort((x, y) => compareIsoWeekKeys(x.week, y.week));
}

function mergeAtRiskRows(insightsPayloads) {
  const byId = new Map();
  for (const data of insightsPayloads) {
    const batchName = data?.batchName ?? 'Batch';
    for (const row of data?.atRiskList ?? []) {
      const id = row.studentId != null ? String(row.studentId) : '';
      if (!id) continue;
      const next = { ...row, sourceBatch: batchName };
      const prev = byId.get(id);
      if (!prev || (row.daysInactive ?? 0) > (prev.daysInactive ?? 0)) {
        byId.set(id, next);
      }
    }
  }
  return [...byId.values()].sort((a, b) => (b.daysInactive ?? 0) - (a.daysInactive ?? 0));
}

function formatInactiveLabel(daysInactive) {
  if (daysInactive == null) return 'Inactive';
  if (daysInactive >= NEVER_SUBMITTED_DAYS) return 'Never submitted';
  return `Inactive for ${daysInactive} day${daysInactive === 1 ? '' : 's'}`;
}

function formatLastActiveShort(lastActive) {
  if (!lastActive || typeof lastActive !== 'string') return '—';
  try {
    const d = new Date(lastActive);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function isSevereAtRisk(daysInactive) {
  return daysInactive > 10 || daysInactive >= NEVER_SUBMITTED_DAYS;
}

// function topPerformersFromProgress(progressPayload, limit = 5) {
//   const rows = progressPayload?.students ?? [];
//   const scored = rows.map((row) => {
//     const solvedSum = (row.assignments ?? []).reduce((a, x) => a + (Number(x.solvedCount) || 0), 0);
//     return {
//       name: row.student?.name || '—',
//       solvedSum,
//     };
//   });
//   scored.sort((a, b) => b.solvedSum - a.solvedSum);
//   return scored.filter((r) => r.solvedSum > 0).slice(0, limit);
// }

// const ChartTooltip = ({ active, payload, label }) => {
//   if (!active || !payload?.length) return null;
//   return (
//     <Box sx={{ ...dashboardTooltipSurfaceSx, borderRadius: 2, p: 1.5 }}>
//       {label != null && (
//         <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
//           {label}
//         </Typography>
//       )}
//       <Typography variant="body2" fontWeight={600}>
//         Problems solved: {payload[0]?.value ?? 0}
//       </Typography>
//     </Box>
//   );
// };

/**
 * CBC faculty home: coding metrics, weekly activity, performers, and at-risk list.
 * Course / lab links are secondary at the bottom.
 */
const CbcFacultyDashboard = () => {
  const { academicProfile } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [insightsList, setInsightsList] = useState([]);
  // const [
  //   topPerformers, 
  //   setTopPerformers] = useState([]);
  // const [
  //   performersLoading, 
  //   setPerformersLoading] = useState(false);

  const loadInsights = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await getBatches();
      const list = data.batches || [];
      if (!list.length) {
        setInsightsList([]);
        return;
      }

      const settled = await Promise.allSettled(list.map((b) => ctGetBatchInsights(b._id)));
      const ok = settled
        .map((r) => (r.status === 'fulfilled' ? r.value.data : null))
        .filter(Boolean);
      setInsightsList(ok);
      if (ok.length === 0 && settled.some((r) => r.status === 'rejected')) {
        setError('Could not load coding insights.');
      }
    } catch {
      setError('Could not load batches.');
      setInsightsList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  // useEffect(() => {
  //   if (!insightsList.length) {
  //     // setTopPerformers([]);
  //     return;
  //   }
  //   const largest = [...insightsList].sort(
  //     (a, b) => (b.totalStudents ?? 0) - (a.totalStudents ?? 0),
  //   )[0];
  //   const batchId = largest?.batchId;
  //   if (!batchId) return;

  //   let cancelled = false;
  //   // setPerformersLoading(true);
  //   ctGetBatchProgress(batchId)
  //     .then(({ data }) => {
  //       if (!cancelled) setTopPerformers(topPerformersFromProgress(data, 5));
  //     })
  //     .catch(() => {
  //       if (!cancelled) setTopPerformers([]);
  //     })
  //     .finally(() => {
  //       if (!cancelled) setPerformersLoading(false);
  //     });
  //   return () => {
  //     cancelled = true;
  //   };
  // }, [insightsList]);

  const totals = useMemo(() => {
    let totalStudents = 0;
    let activeCoders = 0;
    let atRisk = 0;
    for (const d of insightsList) {
      totalStudents += Number(d.totalStudents) || 0;
      activeCoders += Number(d.activeStudents) || 0;
      atRisk += Number(d.atRiskStudents) || 0;
    }
    const mergedWeeks = mergeWeeklyProgress(insightsList);
    const lastWeek = mergedWeeks.length ? mergedWeeks[mergedWeeks.length - 1] : null;
    const weeklySolved = lastWeek ? lastWeek.solved : null;
    const chartWeeks = mergedWeeks.slice(-8);
    return { totalStudents, activeCoders, atRisk, weeklySolved, chartWeeks, lastWeekLabel: lastWeek?.label };
  }, [insightsList]);

  const atRiskMerged = useMemo(() => mergeAtRiskRows(insightsList), [insightsList]);

  return (
    <Box>
      {/* <InstitutionPageHeader /> */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          mb: 0.5,
        }}
      >
        <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>
          Dashboard
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={formatAcademicDashboardChipLabel(academicProfile)}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 640 }}>
        Coding progress across your batches — who is active, what was solved this week, and who needs a
        nudge.
      </Typography>

      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            to="/faculty/coding-dashboard"
            icon={<PeopleOutlineOutlinedIcon fontSize="small" />}
            label="Total students coding"
            value={insightsList.length ? totals.totalStudents : loading ? null : '0'}
            loading={loading}
            hint="Enrolled in your batches"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            to="/faculty/coding-dashboard"
            icon={<CheckCircleOutlineRoundedIcon fontSize="small" />}
            label="Problems solved (weekly)"
            value={totals.weeklySolved != null ? totals.weeklySolved : loading ? null : '0'}
            loading={loading}
            hint={
              totals.lastWeekLabel
                ? `ISO week ${totals.lastWeekLabel} · assigned problems`
                : 'Assigned problems · distinct solves'
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            to="/faculty/coding/activity"
            icon={<BoltRoundedIcon fontSize="small" />}
            label="Active coders"
            value={insightsList.length ? totals.activeCoders : loading ? null : '0'}
            loading={loading}
            hint="Submitted in the last 7 days"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            to="/faculty/coding-dashboard"
            icon={<WarningAmberRoundedIcon fontSize="small" />}
            label="At-risk students"
            value={insightsList.length ? totals.atRisk : loading ? null : '0'}
            loading={loading}
            hint="Stale or no submissions"
          />
        </Grid>
      </Grid>

      {/* <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={8}>
          <GlassCard hoverable={false} sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '-0.01em', mb: 0.5 }}>
                Weekly coding activity
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Unique solves on batch-assigned problems per ISO week (aggregated across batches)
              </Typography>
              {loading ? (
                <Skeleton variant="rounded" width="100%" height={260} sx={{ borderRadius: 2 }} />
              ) : totals.chartWeeks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" py={6} textAlign="center">
                  No weekly solve data yet. Add assignments with problems, or open the{' '}
                  <Button component={RouterLink} to="/faculty/coding-dashboard" size="small" sx={{ p: 0, minWidth: 0 }}>
                    coding dashboard
                  </Button>{' '}
                  to get started.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={totals.chartWeeks} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis
                      allowDecimals={false}
                      width={44}
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      tickFormatter={(v) => String(Math.round(Number(v)))}
                    />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                    <Bar dataKey="solved" name="Solves" fill={PRIMARY} radius={[4, 4, 0, 0]} maxBarSize={48} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </GlassCard>
        </Grid>
        <Grid item xs={12} lg={4}>
          <GlassCard hoverable={false} sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Stack direction="row" alignItems="center" gap={1} mb={2}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: 'action.hover',
                    color: 'warning.main',
                    display: 'flex',
                  }}
                >
                  <EmojiEventsRoundedIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
                  Top performers
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                By assignment problems solved (largest batch)
              </Typography>
              {loading || performersLoading ? (
                <Stack spacing={1.5}>
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rounded" height={52} sx={{ borderRadius: 2 }} />
                  ))}
                </Stack>
              ) : topPerformers.length === 0 ? (
                <Typography variant="body2" color="text.secondary" py={2}>
                  No solve data yet for the primary batch, or progress is still syncing.
                </Typography>
              ) : (
                <Stack spacing={1.25}>
                  {topPerformers.map((row, idx) => (
                    <Box
                      key={`${row.name}-${idx}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        py: 1.25,
                        px: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: (theme) =>
                          theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'grey.50',
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="center" minWidth={0}>
                        <Typography
                          variant="caption"
                          fontWeight={800}
                          color="primary.main"
                          sx={{ width: 22, flexShrink: 0 }}
                        >
                          {idx + 1}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} noWrap title={row.name}>
                          {row.name}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" fontWeight={700} color="primary.main" flexShrink={0}>
                        {row.solvedSum} solved
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </GlassCard>
        </Grid>
      </Grid> */}

      <GlassCard hoverable={false} sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Stack direction="row" alignItems="center" gap={1} mb={2}>
            <ErrorOutlineRoundedIcon sx={{ fontSize: 22, color: 'error.main' }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
              Students needing attention
            </Typography>
          </Stack>
          {loading ? (
            <Stack spacing={1.25}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} variant="rounded" height={64} sx={{ borderRadius: 2 }} />
              ))}
            </Stack>
          ) : atRiskMerged.length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={2} textAlign="center">
              No at-risk students right now.
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              {atRiskMerged.slice(0, 8).map((row, idx) => {
                const severe = isSevereAtRisk(row.daysInactive);
                return (
                  <Box
                    key={String(row.studentId ?? idx)}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      py: 1.25,
                      px: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: severe ? 'error.main' : 'divider',
                      borderLeftWidth: severe ? 4 : 1,
                      borderLeftColor: severe ? 'error.main' : undefined,
                      bgcolor: (theme) =>
                        severe
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(224, 49, 49, 0.12)'
                            : 'rgba(224, 49, 49, 0.06)'
                          : theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.03)'
                            : 'grey.50',
                    }}
                  >
                    <ErrorOutlineRoundedIcon
                      sx={{
                        fontSize: 22,
                        color: 'error.main',
                        flexShrink: 0,
                        mt: 0.15,
                        opacity: severe ? 1 : 0.85,
                      }}
                    />
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" fontWeight={700} noWrap title={row.name}>
                        {row.name || '—'}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={severe ? 700 : 600}
                        color={severe ? 'error.main' : 'text.primary'}
                        sx={{ mt: 0.25 }}
                      >
                        {formatInactiveLabel(row.daysInactive)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                        Last active: {formatLastActiveShort(row.lastActive)} · {row.sourceBatch}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </GlassCard>

      <Box
        sx={{
          pt: 1,
          pb: 2,
          px: 0,
          opacity: 0.85,
        }}
      >
        <Divider sx={{ mb: 2 }} />
        <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.08} sx={{ mb: 1.5, display: 'block' }}>
          Courses & labs
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Button
            component={RouterLink}
            to="/faculty/courses"
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<SchoolOutlinedIcon fontSize="inherit" />}
            sx={{ textTransform: 'none', fontWeight: 600, borderColor: 'divider' }}
          >
            My courses
          </Button>
          <Button
            component={RouterLink}
            to="/faculty/lab-internals"
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<ScienceOutlinedIcon fontSize="inherit" />}
            sx={{ textTransform: 'none', fontWeight: 600, borderColor: 'divider' }}
          >
            Lab internals
          </Button>
          <Button
            component={RouterLink}
            to="/faculty/grading"
            size="small"
            variant="outlined"
            color="inherit"
            sx={{ textTransform: 'none', fontWeight: 600, borderColor: 'divider' }}
          >
            Grading
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default CbcFacultyDashboard;
